import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewModelCode, editModelCode, getAllModelCodes } from "../../../redux/features/modelCodes/modelCodesService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { getAllModels } from "../../../redux/features/models/modelsService";
import TableSkeleton from "../../../components/Skeleton";

const ModelCode = () => {

    const dispatch = useDispatch();
    const modelDropdownRef = useRef(null);
    const modelEditDropdownRef = useRef(null);

    const { isModelCodesLoading, pageCount, modelCodes } = useSelector((state) => state.modelCode.allModelCodes);
    const { isModelCodeCreating } = useSelector((state) => state.modelCode.newModelCode);
    const { isEditingModelCode } = useSelector((state) => state.modelCode.editedModelCode);
    const { models } = useSelector((state) => state.model.allModels);

    const [formData, setFormData] = useState({
        name: '',
        modelId: ''
    });

    const [errorMessages, setErrorMessages] = useState({
        name: '',
        modelId: ''
    });
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [initialModel, setInitialModel] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        modelId: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMake, setSelectedMake] = useState({
        id: '',
        name: ''
    });
    const [selectedEditMake, setSelectedEditMake] = useState({
        id: '',
        name: ''
    });

    const fetchAllModelCodes = useCallback(() => {
        dispatch(getAllModelCodes({
            skip: skip,
            limit,
            search: searchQuery
        }));
    }, [dispatch, skip, searchQuery]);

    const fetchAllActiveModels = useCallback(() => {
        dispatch(getAllModels({
            status: true,
        }));
    }, [dispatch]);

    useEffect(() => {
        fetchAllModelCodes();
        fetchAllActiveModels();
    }, [fetchAllModelCodes, fetchAllActiveModels]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleSelect = (option) => {
        setFormData({ ...formData, modelId: option.id });
        setErrorMessages({ ...errorMessages, modelId: "" });
        setSelectedMake({
            id: option.id,
            name: option.name
        });
    };


    // Validate input
    const isValidModel = () => {
        const errors = {};
        const trimmedName = formData.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!formData.modelId) {
            errors.modelId = "Model is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewModelCode = async () => {
        if (isValidModel(formData)) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewModelCode(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                    makeId: ''
                });
                setSelectedMake({
                    id: '',
                    name: ''
                })
                fetchAllModelCodes();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit model
    const handleSelectedModelChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedModel((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleEditSelect = (option) => {
        setSelectedModel({ ...selectedModel, modelId: option.id });
        setEditErrorMsgs({ ...editErrorMsgs, modelId: "" });
        setSelectedEditMake({
            id: option.id,
            name: option.name
        });
    };

    const isValidEditModel = () => {
        const errors = {};
        const trimmedName = selectedModel.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialModel[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editModelDetails = async () => {
        if (isValidEditModel(selectedModel)) {
            const data = getChangedValues(selectedModel);
            const hasChanges = Object.keys(data).some(key => key !== 'id');

            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editModelCode(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedModel(null);
                    setInitialModel(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllModelCodes();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate ModelCode
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedModelId, setSelectedModelId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editModelCode({ id: selectedModelId, status: true })).unwrap();
            setSelectedModelId(null);
            setInitialModel(null);
            showToast('success', response.message);
            fetchAllModelCodes();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editModelCode({ id: selectedModelId, status: false })).unwrap();
            setSelectedModelId(null);
            setInitialModel(null);
            showToast('success', response.message);
            fetchAllModelCodes();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Pagination
    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Model Code</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Model Code'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Model<span className={`required-star`}>*</span>
                                    </label>
                                    <Dropdown
                                        ref={modelDropdownRef}
                                        options={models}
                                        placeholder="Choose an Option"
                                        onSelect={handleSelect}
                                        value={selectedMake.name}
                                    />
                                    {errorMessages.modelId && (
                                        <p className={`text-danger`}>{errorMessages.modelId}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewModelCode}
                        >
                            {isModelCodeCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Model Codes</h1>
                    <SearchBar onSearch={(query) => setSearchQuery(query)} />
                </div>
                <div className={`search-card-body`}>
                    {isModelCodesLoading ?
                      <TableSkeleton columns={["Make", "Model", "Model Code", "Status", "Actions"]} rowCount={10} height={20}/>
                        : modelCodes.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Make</th>
                                            <th>Model</th>
                                            <th>Model Code</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {modelCodes?.map((modelcode) => (
                                            <tr key={modelcode.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {/* <img src={modelcode.model.make.image_url} alt="make"/> */}
                                                    {modelcode.model.make.name}
                                                </td>
                                                <td>{modelcode.model.name}</td>
                                                <td>{modelcode.name}</td>
                                                <td>{modelcode.status ? "Active" : "Inactive"}</td>
                                                <td className={`actionsBtn`}>
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        setSelectedModel(modelcode);
                                                        setInitialModel(modelcode);
                                                        setSelectedEditMake(modelcode.model);
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            model_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Edit</button>
                                                    <button
                                                        className={modelcode.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedModelId(modelcode.id);
                                                            modelcode.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{modelcode.status ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Model Code</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='name'
                                                name='name'
                                                value={selectedModel.name || ''}
                                                onChange={handleSelectedModelChange}
                                                placeholder='Enter ModelCode Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Model<span className={`required-star`}>*</span>
                                            </label>
                                            <Dropdown
                                                ref={modelEditDropdownRef}
                                                options={models}
                                                placeholder="Choose an Option"
                                                onSelect={handleEditSelect}
                                                value={selectedEditMake.name}
                                            />
                                            {editErrorMsgs.modelId && (
                                                <p className={`text-danger`}>{editErrorMsgs.modelId}</p>
                                            )}
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editModelDetails}
                                    >
                                        {isEditingModelCode ? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='ModelCode Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the ModelCode?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='ModelCode Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the ModelCode?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default ModelCode;
